import { useCallback, useEffect, useRef, useState } from 'react';
import { ALargeSmall, Trash2 } from 'lucide-react';
import type { CaixaTextoPlano } from '../../types/caixaTextoPlano';
import {
  caixaParaPx,
  pxParaCaixa,
  redimensionarCaixaTela,
  RESIZE_HANDLES,
  type CaixaPx,
  type ResizeHandle,
} from '../../utils/caixaTextoPlanoCoords';

const CHAPA_OFFSET_X = 25;
const CHAPA_OFFSET_Y = 20;
const MIN_W_PX = 24;
const MIN_H_PX = 18;

type DragMode = 'move' | 'resize';

interface Props {
  caixas: CaixaTextoPlano[];
  onChange: (caixas: CaixaTextoPlano[]) => void;
  chapaWidth: number;
  chapaHeight: number;
  selectedId: string | null;
  onSelectId: (id: string | null) => void;
  rotacaoDesenho?: number;
}

interface ParentMetrics {
  centerX: number;
  centerY: number;
  width: number;
  height: number;
}

interface DragState {
  id: string;
  mode: DragMode;
  startX: number;
  startY: number;
  orig: CaixaTextoPlano;
  origPx: CaixaPx;
  parent: ParentMetrics;
  handle?: ResizeHandle;
}

function clampFont(n: number) {
  return Math.min(48, Math.max(6, n));
}

function clientParaAreaLocalPx(
  clientX: number,
  clientY: number,
  rotacaoDeg: number,
  parent: ParentMetrics
) {
  const vx = clientX - parent.centerX;
  const vy = clientY - parent.centerY;
  const rad = (-rotacaoDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const px = vx * cos - vy * sin + parent.width / 2;
  const py = vx * sin + vy * cos + parent.height / 2;
  return { x: px - CHAPA_OFFSET_X, y: py - CHAPA_OFFSET_Y };
}

function lerMetricasPai(areaEl: HTMLElement | null): ParentMetrics | null {
  const parent = areaEl?.parentElement;
  if (!parent) return null;
  const rect = parent.getBoundingClientRect();
  return {
    centerX: rect.left + rect.width / 2,
    centerY: rect.top + rect.height / 2,
    width: parent.offsetWidth,
    height: parent.offsetHeight,
  };
}

export default function CaixasTextoPlanoOverlay({
  caixas,
  onChange,
  chapaWidth,
  chapaHeight,
  selectedId,
  onSelectId,
  rotacaoDesenho = 0,
}: Props) {
  const rotacaoNormalizada = ((rotacaoDesenho % 360) + 360) % 360;
  const contrarrotacao = rotacaoNormalizada ? `rotate(-${rotacaoNormalizada}deg)` : undefined;
  const areaRef = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<DragState | null>(null);

  const atualizarCaixa = useCallback(
    (id: string, patch: Partial<CaixaTextoPlano>) => {
      onChange(caixas.map((c) => (c.id === id ? { ...c, ...patch } : c)));
    },
    [caixas, onChange]
  );

  const removerCaixa = useCallback(
    (id: string) => {
      onChange(caixas.filter((c) => c.id !== id));
      if (selectedId === id) onSelectId(null);
    },
    [caixas, onChange, onSelectId, selectedId]
  );

  const ajustarFonte = useCallback(
    (id: string, delta: number) => {
      const caixa = caixas.find((c) => c.id === id);
      if (!caixa) return;
      atualizarCaixa(id, { fontSizePt: clampFont(caixa.fontSizePt + delta) });
    },
    [atualizarCaixa, caixas]
  );

  const iniciarDrag = useCallback(
    (
      id: string,
      mode: DragMode,
      clientX: number,
      clientY: number,
      orig: CaixaTextoPlano,
      handle?: ResizeHandle
    ) => {
      const parent = lerMetricasPai(areaRef.current);
      if (!parent) return;
      setDrag({
        id,
        mode,
        startX: clientX,
        startY: clientY,
        orig,
        origPx: caixaParaPx(orig, chapaWidth, chapaHeight),
        parent,
        handle,
      });
    },
    [chapaWidth, chapaHeight]
  );

  useEffect(() => {
    if (!drag) return;

    const onMove = (e: MouseEvent) => {
      const startLocal = clientParaAreaLocalPx(drag.startX, drag.startY, rotacaoNormalizada, drag.parent);
      const currLocal = clientParaAreaLocalPx(e.clientX, e.clientY, rotacaoNormalizada, drag.parent);
      const dx = currLocal.x - startLocal.x;
      const dy = currLocal.y - startLocal.y;
      const o = drag.orig;

      if (drag.mode === 'move') {
        const moved = caixaParaPx(o, chapaWidth, chapaHeight);
        moved.x = drag.origPx.x + dx;
        moved.y = drag.origPx.y + dy;
        onChange(
          caixas.map((c) =>
            c.id === drag.id ? { ...c, ...pxParaCaixa(moved, chapaWidth, chapaHeight) } : c
          )
        );
      } else if (drag.handle) {
        const dxTela = e.clientX - drag.startX;
        const dyTela = e.clientY - drag.startY;
        const resized = redimensionarCaixaTela(
          drag.origPx,
          drag.handle,
          dxTela,
          dyTela,
          rotacaoNormalizada,
          MIN_W_PX,
          MIN_H_PX
        );
        onChange(
          caixas.map((c) =>
            c.id === drag.id ? { ...c, ...pxParaCaixa(resized, chapaWidth, chapaHeight) } : c
          )
        );
      }
    };

    const onUp = () => setDrag(null);

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [drag, caixas, onChange, chapaWidth, chapaHeight, rotacaoNormalizada]);

  if (caixas.length === 0) return null;

  return (
    <div
      ref={areaRef}
      className="absolute z-20 overflow-visible"
      style={{ left: CHAPA_OFFSET_X, top: CHAPA_OFFSET_Y, width: chapaWidth, height: chapaHeight }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onSelectId(null);
      }}
    >
      {caixas.map((caixa) => {
        const selecionada = selectedId === caixa.id;
        return (
          <div
            key={caixa.id}
            className="absolute overflow-visible"
            style={{
              left: `${caixa.xPct}%`,
              top: `${caixa.yPct}%`,
              width: `${caixa.wPct}%`,
              height: `${caixa.hPct}%`,
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
              onSelectId(caixa.id);
            }}
          >
            <div
              className={`relative flex h-full w-full flex-col overflow-visible ${
                selecionada ? 'z-30' : ''
              }`}
              style={{
                transform: contrarrotacao,
                transformOrigin: 'center center',
                writingMode: 'horizontal-tb',
              }}
            >
              <div
                className={`relative flex h-full w-full flex-col overflow-visible rounded border-2 bg-white/90 shadow-sm ${
                  selecionada ? 'border-[#009b3a] ring-1 ring-[#009b3a]/40' : 'border-slate-400/80'
                }`}
              >
                <div
                  className={`flex shrink-0 items-center justify-between gap-0.5 border-b px-1 py-0.5 ${
                    selecionada ? 'bg-[#e8f5ec]' : 'bg-slate-100'
                  } cursor-move`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onSelectId(caixa.id);
                    iniciarDrag(caixa.id, 'move', e.clientX, e.clientY, { ...caixa });
                  }}
                >
                  <span className="truncate text-[9px] font-semibold text-neutral-600">Texto</span>
                  <div className="flex items-center gap-0.5">
                    <button
                      type="button"
                      title="Aumentar fonte"
                      className="rounded p-0.5 hover:bg-white/80"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        ajustarFonte(caixa.id, 1);
                      }}
                    >
                      <ALargeSmall className="h-3.5 w-3.5 text-[#009b3a]" />
                    </button>
                    <button
                      type="button"
                      title="Diminuir fonte"
                      className="rounded px-0.5 text-[10px] font-bold leading-none text-neutral-600 hover:bg-white/80"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        ajustarFonte(caixa.id, -1);
                      }}
                    >
                      A−
                    </button>
                    <button
                      type="button"
                      title="Remover caixa"
                      className="rounded p-0.5 text-red-600 hover:bg-red-50"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        removerCaixa(caixa.id);
                      }}
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </div>
                <textarea
                  value={caixa.texto}
                  onChange={(e) => atualizarCaixa(caixa.id, { texto: e.target.value })}
                  placeholder="Digite aqui…"
                  className="min-h-0 flex-1 resize-none overflow-auto border-0 bg-transparent p-1 leading-snug text-black outline-none whitespace-pre-wrap break-words"
                  style={{ fontSize: `${caixa.fontSizePt}pt`, writingMode: 'horizontal-tb' }}
                  onMouseDown={(e) => e.stopPropagation()}
                />
              </div>

              {selecionada &&
                RESIZE_HANDLES.map((h) => (
                  <div
                    key={h.id}
                    className={`absolute z-40 h-2.5 w-2.5 rounded-sm border border-white bg-[#009b3a] shadow ${h.className}`}
                    style={{ cursor: h.cursor }}
                    title="Redimensionar"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onSelectId(caixa.id);
                      iniciarDrag(caixa.id, 'resize', e.clientX, e.clientY, { ...caixa }, h.id);
                    }}
                  />
                ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export { CHAPA_OFFSET_X, CHAPA_OFFSET_Y };
